import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Pagination } from '@/common/types';
import { API_URL } from '@/dtos';
import { PlanTypesEnum } from '@/modules/plan/plan.types';

interface ServicePayload {
  id?: number;
  name?: string;
  category_type?: PlanTypesEnum;
  primary?: boolean;
  displayed?: boolean;
}

export interface Service {
  id: string;
  name?: string;
  categoryType?: PlanTypesEnum;
  primary?: boolean;
  displayed?: boolean;
}

type ServicesGroupedByCategoryType = {
  [key in PlanTypesEnum]: Service[];
};

const QUERY_KEY = 'GET_ALL_SERVICES';

// TODO: Improve pagination handling — currently fetching all services in a single request
const MAX_PAGE_SIZE = 3000;

const formatData = (services: ServicePayload[], name: string): Service[] =>
  services
    .filter((service) => service.name?.toLowerCase()?.includes(name))
    .map((service: ServicePayload) => ({
      id: toString(service?.id),
      name: service?.name,
      categoryType: service?.category_type,
      primary: service?.primary,
      displayed: service?.displayed,
    }));

const useGetAllServicesQuery = (name: string = '') => {
  const { isLoading, isSuccess, data } = useQuery([QUERY_KEY], () =>
    api.get<Pagination<ServicePayload>>(API_URL.V2_SERVICES_ALL, {
      params: {
        page: 0,
        size: MAX_PAGE_SIZE,
      },
    })
  );

  const formattedData = useMemo(
    () => formatData(data?.data?.content || [], name.toLowerCase()),
    [data, name]
  );
  const servicesGroupedByCategoryType = useMemo(
    () =>
      formattedData.reduce((acc, val) => {
        if (!val?.categoryType) return acc;

        const categoryType = val.categoryType;

        if (!acc[categoryType]) {
          acc[categoryType] = [];
        }

        acc[categoryType].push(val);

        return acc;
      }, {} as ServicesGroupedByCategoryType),
    [formattedData]
  );

  return {
    formattedData,
    servicesGroupedByCategoryType,
    isLoading,
    isSuccess,
  };
};

export default useGetAllServicesQuery;
